/**
 * Fetches and injects the SVG sprite inline at the top of <body>.
 *
 * Injecting the sprite inline removes cross-origin restrictions when
 * referencing icon symbols via <use href="#id">. The container element
 * is visually hidden and marked aria-hidden so it has no impact on
 * assistive technologies.
 *
 * Template usage after injection:
 *   <svg aria-hidden="true" focusable="false" width="24" height="24">
 *     <use href="#icon-name"></use>
 *   </svg>
 *
 * @param {string} spriteUrl - Absolute or root-relative URL to sprite.svg
 * @returns {Promise<void>}
 */

const CUTTED_M_CLASS_TO_SYMBOL = {
  'cutted-m': '#cutted-m',
  'cutted-m-green-small': '#cutted-m-green-small',
  'cutted-m-white-small': '#cutted-m-white-small',
};

/**
 * Ensures cutted-m class variants point to the matching symbol ids.
 *
 * CMS templates can accidentally reuse the same href for different visual
 * variants; this normalizes them at runtime and logs a warning for visibility.
 *
 * @returns {void}
 */
function normalizeCuttedMIconReferences() {
  Object.entries(CUTTED_M_CLASS_TO_SYMBOL).forEach(([className, expectedHref]) => {
    document.querySelectorAll(`svg.${className}`).forEach((svgElement) => {
      let useElement = svgElement.querySelector('use');

      if (!useElement) {
        useElement = document.createElementNS('http://www.w3.org/2000/svg', 'use');
        svgElement.appendChild(useElement);
      }

      const currentHref = useElement.getAttribute('href') || useElement.getAttribute('xlink:href');

      if (currentHref !== expectedHref) {
        console.warn(`[svg-sprite] Normalized ${className} icon href from "${currentHref}" to "${expectedHref}".`);
      }

      useElement.setAttribute('href', expectedHref);
      useElement.setAttribute('xlink:href', expectedHref);
    });
  });
}

async function injectSvgSprite(spriteUrl) {
  try {
    const response = await fetch(spriteUrl);

    if (!response.ok) {
      throw new Error(`HTTP ${response.status} – ${response.statusText}`);
    }

    const svgText = await response.text();

    const container = document.createElement('div');
    container.setAttribute('aria-hidden', 'true');
    container.className = 'svg-sprite-container';
    container.innerHTML = svgText;

    document.body.insertBefore(container, document.body.firstChild);
    normalizeCuttedMIconReferences();
  } catch (error) {
    console.error(`[svg-sprite] Failed to inject sprite from "${spriteUrl}":`, error);
  }
}

export { injectSvgSprite };
